<template>
  <div class="page-container report-workflow-page" v-loading="loading">
    <PageHeader title="申请报告生成">
      <template #actions>
        <el-button size="small" icon="el-icon-back" @click="goBack">返回列表</el-button>
      </template>
    </PageHeader>

    <div class="workflow-shell">
      <section class="workflow-hero">
        <div class="workflow-hero__content">
          <div class="workflow-kicker">Report Workflow</div>
          <h2>申请、审核、生成一站式流程</h2>
          <p>按步骤完成报告包申请、审核等待、考勤表整合、评估表处理与最终报告生成。</p>
        </div>
        <div class="workflow-summary-card">
          <div class="workflow-summary-card__label">当前申请</div>
          <div class="workflow-summary-card__value">{{ summaryNo }}</div>
          <div class="workflow-summary-card__meta">
            <span>{{ summaryCourse }}</span>
            <span>{{ summaryCustomer }}</span>
          </div>
          <span class="workflow-status-chip" :class="statusChipClass">{{ statusText }}</span>
        </div>
      </section>

      <section class="workflow-steps-card">
        <el-steps class="workflow-steps" :active="activeStep" align-center finish-status="success">
          <el-step
            v-for="(step, index) in steps"
            :key="step.title"
            :title="step.title"
            :icon="step.icon"
            :class="{ 'is-clickable': canSkipToStep(index) }"
            @click.native="canSkipToStep(index) && handleStepClick(index)"
          >
            <template #description>
              <div v-if="index === 3" class="workflow-attendance-step-desc">
                <span class="workflow-data-pill" :class="signUploaded ? 'is-success' : 'is-danger'">
                  <i class="workflow-data-dot"></i><b>签到表</b><em>{{ signUploaded ? '已上传' : '未上传' }}</em>
                </span>
                <span class="workflow-data-pill" :class="examUploaded ? 'is-success' : 'is-danger'">
                  <i class="workflow-data-dot"></i><b>考试原始数据</b><em>{{ examUploaded ? '已上传' : '未上传' }}</em>
                </span>
              </div>
              <div v-else-if="index === 4" class="workflow-attendance-step-desc">
                <span class="workflow-data-pill" :class="ratingUploaded ? 'is-success' : 'is-danger'">
                  <i class="workflow-data-dot"></i><b>评估表</b><em>{{ ratingUploaded ? '已上传' : '未上传' }}</em>
                </span>
              </div>
              <div v-else-if="index === 5" class="workflow-attendance-step-desc">
                <span class="workflow-data-pill" :class="reportGenerated ? 'is-success' : 'is-danger'">
                  <i class="workflow-data-dot"></i><b>报告</b><em>{{ reportGenerated ? '已生成' : '未生成' }}</em>
                </span>
              </div>
              <span v-else>{{ step.description }}</span>
            </template>
          </el-step>
        </el-steps>
      </section>

      <!-- 模板未上传提示（步骤条下方） -->
      <section v-if="templateWarning" class="workflow-template-warning">
        <div class="workflow-template-warning__icon"><i class="el-icon-warning-outline"></i></div>
        <div class="workflow-template-warning__body">
          <div class="workflow-template-warning__title">{{ templateWarning.title }}</div>
          <div class="workflow-template-warning__desc">{{ templateWarning.desc }}</div>
        </div>
      </section>

      <section class="workflow-main-card">
        <div class="workflow-main-card__header">
          <div>
            <div class="workflow-main-card__eyebrow">{{ currentStepLabel }}</div>
            <h3>{{ currentStepTitle }}</h3>
          </div>
          <div v-if="rrmaStatus === 2" class="workflow-review-card">
            <div class="workflow-review-card__icon"><i class="el-icon-loading"></i></div>
            <div>
              <div class="workflow-review-card__title">管理员审核中</div>
              <div class="workflow-review-card__desc">申请已提交，当前不可编辑；审核通过后将自动开放考勤表、评估表和报告生成步骤。</div>
            </div>
          </div>
          <el-alert v-else-if="rrmaStatus === 3" type="success" show-icon :closable="false" title="申请已审核通过，可继续完成考勤表、评估表和总结报告生成。" />
        </div>

        <report-apply-form
          v-if="activeStep <= 2"
          ref="applyForm"
          :rrma-id="rrmaId"
          :detail-data="applyDetail"
          :readonly="readonlyApplyForm"
          @saved="handleApplySaved"
          @submitted="handleApplySubmitted"
          @detail-loaded="handleDetailLoaded"
          @status-change="handleStatusChange"
        />

        <report-attendance-step
          v-else-if="activeStep === 3"
          ref="attendanceStep"
          :rrma-id="rrmaId"
          :initial-record="generationRecord"
          @busy-change="handleStepBusyChange"
          @progress-change="handleGenerationProgressChange"
        />

        <report-gen-evaluation
          v-else-if="activeStep === 4"
          :rrma-id="rrmaId"
          :rating-up-type="ratingUpType"
          :detail-data="applyDetail"
          @busy-change="handleStepBusyChange"
          @progress-change="loadGenerationProgress"
        />

        <report-gen-summary v-else-if="activeStep === 5" :rrma-id="rrmaId" />
      </section>
      <section class="workflow-actions">
        <div class="workflow-actions__hint" :class="{ 'is-reviewing': rrmaStatus === 2 }">
          <i :class="rrmaStatus === 2 ? 'el-icon-loading' : 'el-icon-info'"></i>
          <span>{{ actionHint }}</span>
        </div>
        <div class="workflow-actions__buttons">
          <el-button size="small" @click="goBack">返回列表</el-button>
          <el-button v-if="canSaveDraft" size="small" :loading="submitLoading" @click="saveDraft">暂存编辑</el-button>
          <el-button v-if="canSubmitApply" type="primary" size="small" :loading="submitLoading" @click="submitApply">提交审核</el-button>
          <el-button v-if="rrmaStatus === 2" type="info" size="small" icon="el-icon-refresh" @click="loadApplyDetail">刷新审核状态</el-button>
          <el-button v-if="canClearGenerationData" type="danger" size="small" :loading="clearGenerationLoading" @click="handleClearGenerationData">清空生成数据</el-button>
          <el-button v-if="rrmaStatus === 3 && activeStep > 3" size="small" @click="prevGenerateStep">上一步</el-button>
          <el-button v-if="rrmaStatus === 3 && activeStep < 5" type="primary" size="small" :disabled="stepBusy" @click="nextGenerateStep">下一步</el-button>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import PageHeader from '@/components/PageHeader.vue'
import ReportApplyForm from './reportApplyForm.vue'
import ReportAttendanceStep from './reportAttendanceStep.vue'
import ReportGenEvaluation from './reportGenEvaluation.vue'
import ReportGenSummary from './reportGenSummary.vue'
import {
  getRtReportModelApplyDetailsUser,
  getRtReportModelUserSignMergeList,
  deleteRtReportModelUserSignMergeAll
} from '@/api/api'

const STATUS_MAP = { 1: '待提交', 2: '待审核', 3: '已发包' }

export default {
  name: 'ReportApplyWorkflow',
  components: { PageHeader, ReportApplyForm, ReportAttendanceStep, ReportGenEvaluation, ReportGenSummary },
  data () {
    return {
      mode: 'add',
      rrmaId: null,
      applyDetail: null,
      rrmaStatus: 0,
      activeStep: 0,
      loading: false,
      submitLoading: false,
      clearGenerationLoading: false,
      stepBusy: false,
      ratingUpType: null,
      generationRecord: null,
      generationState: {
        attendanceDone: false,
        evaluationDone: false,
        reportDone: false
      },
      steps: [
        { title: '填写申请', description: '资料与规则', icon: 'el-icon-edit-outline' },
        { title: '提交审核', description: '提交到管理员', icon: 'el-icon-s-promotion' },
        { title: '审核通过', description: '等待发包', icon: 'el-icon-circle-check' },
        { title: '生成考勤表', description: '签到表与考勤表', icon: 'el-icon-upload2' },
        { title: '生成评估表', description: '评估数据处理', icon: 'el-icon-data-analysis' },
        { title: '生成报告', description: 'AI 整合导出', icon: 'el-icon-document' }
      ]
    }
  },
  computed: {
    statusText () {
      return this.rrmaStatus ? (STATUS_MAP[this.rrmaStatus] || '未知状态') : '新建申请'
    },
    statusChipClass () {
      return this.rrmaStatus ? `is-status-${this.rrmaStatus}` : 'is-status-new'
    },
    summaryNo () {
      return (this.applyDetail && this.applyDetail.rfiOrderNum) || '新申请'
    },
    summaryCourse () {
      return (this.applyDetail && (this.applyDetail.applyRuleNameCourse || this.applyDetail.rrmaCourseType)) || '待填写课程'
    },
    summaryCustomer () {
      return (this.applyDetail && (this.applyDetail.applyRuleNameCustomer || this.applyDetail.rrmaCustomer)) || '待填写客户'
    },
    currentStepLabel () {
      return `步骤 ${this.activeStep + 1} / ${this.steps.length}`
    },
    currentStepTitle () {
      const step = this.steps[this.activeStep]
      return step ? step.title : '流程处理'
    },
    readonlyApplyForm () {
      return Number(this.rrmaStatus) === 2 || Number(this.rrmaStatus) === 3
    },
    canSaveDraft () {
      return this.activeStep === 0 && !this.readonlyApplyForm
    },
    canSubmitApply () {
      return this.activeStep === 0 && !this.readonlyApplyForm
    },
    canClearGenerationData () {
      return Number(this.rrmaStatus) === 3 && this.activeStep >= 3 && !!(this.generationRecord && this.generationRecord.rrmusmId)
    },
    signUploaded () {
      return !!(this.generationRecord && this.generationRecord.signAddExam)
    },
    examUploaded () {
      // 数据结构变更后，考试原始数据存在 rtReportModelUserSignMergeExamList（多条）；兼容旧数据 examUp
      return this.hasExamData(this.generationRecord)
    },
    ratingUploaded () {
      // 数据结构变更后，评估表存在 rtReportModelUserSignMergeRatingList（多条）；兼容旧数据 ratingUp / 25项列表
      return this.hasRatingData(this.generationRecord)
    },
    reportGenerated () {
      return !!(this.generationRecord && this.generationRecord.wordTextAi)
    },
    // 模板未上传提示（根据 exist 状态判断）
    templateWarning () {
      if (!this.generationRecord || Number(this.rrmaStatus) !== 3) return null
      const rec = this.generationRecord
      if (this.activeStep === 3 && Number(rec.existOne) === 2) {
        return { title: '考勤表模板暂未就绪', desc: '当前课程对应的考勤模板使用频率较低，暂未配置上传。在线生成和导出功能暂时无法使用，如有需要请联系管理员处理。' }
      }
      if (this.activeStep === 4 && Number(rec.existTwo) === 2) {
        return { title: '评估表模板暂未就绪', desc: '当前课程对应的评估模板使用频率较低，暂未配置上传。在线生成和导出功能暂时无法使用，如有需要请联系管理员处理。' }
      }
      if (this.activeStep === 5 && Number(rec.existThree) === 2) {
        return { title: '报告模板暂未开发', desc: '当前报告模板使用频率较低，暂未开发，无法直接在线生成。请您在本平台生成各模块文字内容后，手动粘贴到报告中完成交付。' }
      }
      return null
    },
    actionHint () {
      if (!this.rrmaId) return '填写申请后可先暂存，也可以直接提交审核。'
      if (Number(this.rrmaStatus) === 1) return '当前为待提交状态，可继续编辑、暂存或提交审核。'
      if (Number(this.rrmaStatus) === 2) return '管理员正在审核当前申请，可点击刷新审核状态；通过后继续生成报告。'
      if (this.stepBusy) return '当前步骤正在处理上传、识别或保存，请等待接口完成后再继续下一步。'
      if (this.activeStep === 3) return '先完成签到表识别，再上传考试表生成签到与考勤整合数据。'
      if (this.activeStep === 4) return '处理评估表数据后，继续进入最终总结报告生成。'
      return '完善讲师简介和正文内容后，可导出最终报告。'
    }
  },
  watch: {
    '$route.query': {
      immediate: true,
      handler () {
        this.initPageByRoute()
      }
    }
  },
  methods: {
    initPageByRoute () {
      this.mode = this.$route.query.mode || 'add'
      this.rrmaId = this.$route.query.rrmaId || null
      if (this.rrmaId) {
        this.loadApplyDetail()
      } else {
        this.applyDetail = null
        this.rrmaStatus = 0
        this.activeStep = 0
        this.generationRecord = null
      }
    },
    async loadApplyDetail () {
      if (!this.rrmaId) return
      this.loading = true
      try {
        const res = await getRtReportModelApplyDetailsUser({ rrmaId: this.rrmaId })
        this.applyDetail = res.data || null
        this.rrmaStatus = Number((this.applyDetail && this.applyDetail.rrmaStatus) || 0)
        this.ratingUpType = (this.applyDetail && this.applyDetail.ratingUpType) || null
        await this.loadGenerationProgress()
        this.computeActiveStep()
      } catch (e) {
        this.$message.error('获取申请详情失败')
      } finally {
        this.loading = false
      }
    },
    async loadGenerationProgress () {
      this.generationRecord = null
      if (!this.rrmaId || Number(this.rrmaStatus) !== 3) return
      try {
        const res = await getRtReportModelUserSignMergeList({ rrmaId: this.rrmaId })
        const record = Array.isArray(res.data) ? res.data[res.data.length - 1] : res.data
        this.generationRecord = record || null
        // 数据结构变更后：考试/评估是否上传改用子记录列表判断（兼容旧数据 examUp/ratingUp）
        this.generationState.attendanceDone = !!(record && record.signAddExam && this.hasExamData(record))
        this.generationState.evaluationDone = !!(record && this.hasRatingData(record))
        this.generationState.reportDone = !!(record && (record.wordTextAi || record.wordTeacherAi))
        this.ratingUpType = (record && record.ratingUpType) || this.ratingUpType
      } catch (e) {
        this.generationRecord = null
        this.generationState = { attendanceDone: false, evaluationDone: false, reportDone: false }
      }
    },
    computeActiveStep () {
      if (!this.rrmaId || Number(this.rrmaStatus) === 1) {
        this.activeStep = 0
      } else if (Number(this.rrmaStatus) === 2) {
        this.activeStep = 1
      } else if (Number(this.rrmaStatus) === 3) {
        if (this.mode === 'generate') this.activeStep = 3
        else if (this.generationState.reportDone) this.activeStep = 5
        else if (this.generationState.evaluationDone) this.activeStep = 5
        else if (this.generationState.attendanceDone) this.activeStep = 4
        else this.activeStep = 3
      } else {
        this.activeStep = 0
      }
    },
    handleDetailLoaded (detail) {
      this.handleStatusChange(detail)
    },
    handleStatusChange (detail) {
      this.applyDetail = { ...(this.applyDetail || {}), ...(detail || {}) }
      this.rrmaStatus = Number((detail && detail.rrmaStatus) || this.rrmaStatus || 0)
      if (detail && detail.rrmaId) this.rrmaId = detail.rrmaId
      this.computeActiveStep()
    },
    async handleApplySaved (detail) {
      this.$message.success('申请已暂存')
      this.handleStatusChange(detail)
      this.replaceRoute('edit')
    },
    async handleApplySubmitted (detail) {
      this.$message.success('申请已提交，等待审核')
      this.handleStatusChange(detail)
      this.replaceRoute('edit')
    },
    async saveDraft () {
      this.submitLoading = true
      try {
        await this.$refs.applyForm.saveDraft()
      } finally {
        this.submitLoading = false
      }
    },
    async submitApply () {
      this.submitLoading = true
      try {
        await this.$refs.applyForm.submitApply()
      } finally {
        this.submitLoading = false
      }
    },
    replaceRoute (mode) {
      if (!this.rrmaId) return
      this.$router.replace({ path: '/reportApply/reportApplyWorkflow', query: { mode, rrmaId: this.rrmaId } }).catch(() => {})
    },
    prevGenerateStep () {
      if (this.activeStep > 3) this.activeStep -= 1
    },
    nextGenerateStep () {
      if (this.stepBusy) {
        this.$message.warning('当前步骤正在处理，请等待完成后再继续')
        return
      }
      if (this.activeStep < 5) this.activeStep += 1
    },
    handleStepBusyChange (busy) {
      this.stepBusy = !!busy
    },
    canSkipToStep (index) {
      return Number(this.rrmaStatus) === 3 && index >= 3 && index <= 5 && this.activeStep !== index && !this.stepBusy
    },
    handleStepClick (index) {
      this.activeStep = index
    },
    handleGenerationProgressChange (record) {
      this.generationRecord = record || null
      this.generationState.attendanceDone = !!(record && record.signAddExam && this.hasExamData(record))
      this.generationState.evaluationDone = !!(record && this.hasRatingData(record))
      this.generationState.reportDone = !!(record && (record.wordTextAi || record.wordTeacherAi))
      this.ratingUpType = (record && record.ratingUpType) || this.ratingUpType
    },
    // 是否有考试原始数据：优先用多条子记录列表 rtReportModelUserSignMergeExamList，兼容旧字段 examUp
    hasExamData (record) {
      if (!record) return false
      const list = record.rtReportModelUserSignMergeExamList
      return !!(Array.isArray(list) ? list.length : record.examUp)
    },
    // 是否有评估表数据：优先用多条子记录列表 rtReportModelUserSignMergeRatingList，兼容旧字段 ratingUp / 25项列表
    hasRatingData (record) {
      if (!record) return false
      const list = record.rtReportModelUserSignMergeRatingList
      return !!((Array.isArray(list) ? list.length : false) || record.ratingUp || record.rtReportModelRatingTwentyFiveList)
    },
    async handleClearGenerationData () {
      if (!this.generationRecord || !this.generationRecord.rrmusmId) {
        this.$message.warning('暂无可清空的生成数据')
        return
      }
      try {
        await this.$confirm('确认清空考勤、评估和报告数据？该操作不可恢复。', '清空生成数据', { type: 'warning' })
        this.clearGenerationLoading = true
        await deleteRtReportModelUserSignMergeAll({ rrmusmId: this.generationRecord.rrmusmId })
        this.$message.success('考勤、评估和报告数据已清空')
        await this.loadGenerationProgress()
        if (this.$refs.attendanceStep && this.$refs.attendanceStep.refreshMergeTable) {
          this.$refs.attendanceStep.refreshMergeTable()
        }
        this.computeActiveStep()
      } catch (e) {
        if (e !== 'cancel') this.$message.error(e.message || '清空失败，请稍后重试')
      } finally {
        this.clearGenerationLoading = false
      }
    },
    goBack () {
      this.$router.push('/reportApply/reportApplyList').catch(() => {})
    }
  }
}
</script>

<style lang="scss" scoped>
.report-workflow-page {
  overflow: visible !important;
  background: transparent !important;
  backdrop-filter: none !important;
  padding-bottom: 108px !important;
}

.workflow-shell {
  display: flex;
  flex-direction: column;
  gap: 18px;
  padding-bottom: 96px;
}

.workflow-hero {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 360px;
  gap: 18px;
  padding: 24px;
  border-radius: 22px;
  color: #ffffff;
  background:
    radial-gradient(circle at 12% 18%, rgba(255, 255, 255, 0.28), transparent 30%),
    linear-gradient(118deg, #0f2a4a 0%, #1d4ed8 54%, #0891b2 100%);
  box-shadow: 0 18px 36px rgba(30, 64, 175, 0.18);
}

.workflow-kicker {
  margin-bottom: 8px;
  color: rgba(219, 234, 254, 0.9);
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.workflow-hero__content h2 {
  margin: 0;
  font-size: 26px;
  font-weight: 900;
}

.workflow-hero__content p {
  max-width: 720px;
  margin: 10px 0 0;
  color: rgba(239, 246, 255, 0.9);
  line-height: 1.8;
}

.workflow-summary-card {
  position: relative;
  padding: 18px;
  border: 1px solid rgba(255, 255, 255, 0.24);
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.14);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.22);
  backdrop-filter: blur(14px) saturate(1.18);
}

.workflow-summary-card__label {
  color: rgba(219, 234, 254, 0.86);
  font-size: 12px;
  font-weight: 800;
}

.workflow-summary-card__value {
  margin-top: 8px;
  font-size: 20px;
  font-weight: 900;
}

.workflow-summary-card__meta {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 10px;
  color: rgba(239, 246, 255, 0.86);
  font-size: 13px;
}

.workflow-status-chip {
  display: inline-flex;
  align-items: center;
  margin-top: 14px;
  padding: 6px 12px;
  border-radius: 999px;
  color: #ffffff;
  font-size: 13px;
  font-weight: 850;
  background: rgba(255, 255, 255, 0.18);

  &.is-status-1 { background: rgba(245, 158, 11, 0.88); }
  &.is-status-2 { background: rgba(37, 99, 235, 0.9); }
  &.is-status-3 { background: rgba(16, 185, 129, 0.9); }
}

.workflow-steps-card,
.workflow-main-card,
.workflow-actions {
  border: 1px solid rgba(203, 213, 225, 0.78);
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0 12px 28px rgba(15, 23, 42, 0.07);
}

.workflow-steps-card {
  padding: 28px 26px 24px;
  overflow: hidden;
}

// 模板未上传提示横幅
.workflow-template-warning {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 18px 22px;
  border: 2px solid #f59e0b;
  border-radius: 16px;
  background:
    linear-gradient(135deg, #fffbeb, #fff7ed, #ffffff);
  box-shadow: 0 8px 24px rgba(245, 158, 11, 0.16);
  animation: template-warning-glow 2.5s ease-in-out infinite;
}

@keyframes template-warning-glow {
  0%, 100% { box-shadow: 0 8px 24px rgba(245, 158, 11, 0.16); }
  50% { box-shadow: 0 8px 32px rgba(245, 158, 11, 0.28); }
}

.workflow-template-warning__icon {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: linear-gradient(135deg, #fbbf24, #f59e0b);
  color: #ffffff;
  font-size: 22px;
  box-shadow: 0 6px 14px rgba(245, 158, 11, 0.3);
}

.workflow-template-warning__title {
  font-size: 16px;
  font-weight: 800;
  color: #92400e;
  margin-bottom: 6px;
}

.workflow-template-warning__desc {
  font-size: 13px;
  color: #78350f;
  line-height: 1.7;
}

.workflow-steps {
  width: 100%;
}

::v-deep .workflow-steps .el-step {
  min-width: 0;
}

::v-deep .workflow-steps .el-step.is-clickable {
  cursor: pointer;

  &:hover .el-step__head .el-step__icon {
    transform: scale(1.1);
    box-shadow: 0 0 0 4px rgba(37, 99, 235, 0.12);
  }
}

::v-deep .workflow-steps .el-step__head,
::v-deep .workflow-steps .el-step__main,
::v-deep .workflow-steps .el-step__title,
::v-deep .workflow-steps .el-step__description {
  background: transparent !important;
  background-image: none !important;
  border: 0 !important;
  outline: 0 !important;
  box-shadow: none !important;
}

::v-deep .workflow-steps .el-step__icon {
  width: 34px;
  height: 34px;
  border-width: 2px;
  background: #ffffff !important;
  box-shadow: 0 8px 18px rgba(15, 23, 42, 0.08) !important;
}

::v-deep .workflow-steps .el-step__icon-inner {
  font-size: 18px;
  font-weight: 800;
}

::v-deep .workflow-steps .el-step__line {
  display: block !important;
  height: 2px !important;
  border: 0 !important;
  background: #dbeafe !important;
  background-image: none !important;
  box-shadow: none !important;
}

::v-deep .workflow-steps .el-step__line-inner {
  display: block !important;
  height: 2px !important;
  border: 0 !important;
  background: linear-gradient(90deg, #14b8a6, #2563eb) !important;
  background-image: linear-gradient(90deg, #14b8a6, #2563eb) !important;
}

::v-deep .workflow-steps .el-step__main {
  margin-top: 10px;
  text-align: center;
}

::v-deep .workflow-steps .el-step__title {
  color: #94a3b8;
  font-size: 15px;
  font-weight: 800;
  line-height: 1.35;
}

::v-deep .workflow-steps .el-step__description {
  margin-top: 6px;
  padding: 0 8px;
  color: #94a3b8;
  font-size: 12px;
  line-height: 1.45;
}

::v-deep .workflow-steps .el-step__head.is-process .el-step__icon {
  color: #ffffff;
  border-color: transparent !important;
  background: linear-gradient(135deg, #2563eb, #0891b2) !important;
  box-shadow: 0 12px 24px rgba(37, 99, 235, 0.22) !important;
}

::v-deep .workflow-steps .el-step__head.is-success .el-step__icon {
  color: #ffffff;
  border-color: transparent !important;
  background: linear-gradient(135deg, #0f766e, #14b8a6) !important;
  box-shadow: 0 10px 20px rgba(20, 184, 166, 0.18) !important;
}

::v-deep .workflow-steps .el-step__head.is-wait .el-step__icon,
::v-deep .workflow-steps .el-step__head.is-finish .el-step__icon {
  color: #94a3b8;
  border-color: #e2e8f0 !important;
  background: #ffffff !important;
  box-shadow: 0 8px 18px rgba(15, 23, 42, 0.06) !important;
}

::v-deep .workflow-steps .el-step__head.is-success .el-step__line-inner {
  border-color: transparent !important;
}

::v-deep .workflow-steps .el-step__title.is-process {
  color: #1d4ed8;
}

::v-deep .workflow-steps .el-step__title.is-success {
  color: #0f766e;
}

::v-deep .workflow-steps .el-step__title.is-finish,
::v-deep .workflow-steps .el-step__title.is-wait {
  color: #94a3b8;
}

::v-deep .workflow-steps .el-step__description.is-process {
  color: #475569;
}

::v-deep .workflow-steps .el-step__description.is-success {
  color: #0f766e;
}

::v-deep .workflow-steps .el-step__description.is-finish,
::v-deep .workflow-steps .el-step__description.is-wait {
  color: #94a3b8;
}

.workflow-attendance-step-desc {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
  color: #64748b;
  font-size: 12px;
  line-height: 1.35;
}

.workflow-data-pill {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  min-width: 112px;
  padding: 3px 8px;
  border: 1px solid rgba(148, 163, 184, 0.22);
  border-radius: 999px;
  background: #ffffff;
  box-shadow: 0 6px 14px rgba(15, 23, 42, 0.08);
  white-space: nowrap;

  b {
    color: #334155;
    font-weight: 850;
  }

  em {
    font-style: normal;
    font-weight: 850;
  }
}

.workflow-data-dot {
  width: 10px;
  height: 10px;
  flex: 0 0 auto;
  border-radius: 999px;
}

.workflow-data-pill.is-success {
  border-color: rgba(16, 185, 129, 0.34);
  background: linear-gradient(135deg, #ecfdf5, #ffffff);

  .workflow-data-dot {
    background: #10b981;
    box-shadow: 0 0 0 4px rgba(16, 185, 129, 0.18), 0 0 12px rgba(16, 185, 129, 0.45);
  }

  em { color: #047857; }
}

.workflow-data-pill.is-danger {
  border-color: rgba(239, 68, 68, 0.34);
  background: linear-gradient(135deg, #fef2f2, #ffffff);

  .workflow-data-dot {
    background: #ef4444;
    box-shadow: 0 0 0 4px rgba(239, 68, 68, 0.18), 0 0 12px rgba(239, 68, 68, 0.45);
  }

  em { color: #b91c1c; }
}

.workflow-main-card {
  padding: 20px;
}

.workflow-main-card__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 18px;
  margin-bottom: 18px;

  h3 {
    margin: 4px 0 0;
    color: #0f172a;
    font-size: 22px;
    font-weight: 900;
  }

  .el-alert {
    max-width: 520px;
  }
}

.workflow-main-card__eyebrow {
  color: #2563eb;
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 0.08em;
}

.workflow-review-card {
  display: flex;
  align-items: center;
  gap: 14px;
  width: min(620px, 100%);
  padding: 14px 16px;
  border: 1px solid rgba(59, 130, 246, 0.26);
  border-radius: 16px;
  background:
    radial-gradient(circle at 8% 12%, rgba(96, 165, 250, 0.18), transparent 30%),
    linear-gradient(135deg, rgba(239, 246, 255, 0.98), rgba(224, 242, 254, 0.9));
  box-shadow: 0 12px 26px rgba(37, 99, 235, 0.1);
}

.workflow-review-card__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  flex: 0 0 auto;
  border-radius: 14px;
  color: #ffffff;
  font-size: 22px;
  background: linear-gradient(135deg, #2563eb, #06b6d4);
  box-shadow: 0 10px 22px rgba(37, 99, 235, 0.24);
}

.workflow-review-card__title {
  color: #0f172a;
  font-size: 16px;
  font-weight: 900;
}

.workflow-review-card__desc {
  margin-top: 4px;
  color: #475569;
  font-size: 13px;
  line-height: 1.6;
}

.workflow-actions {
  position: fixed !important;
  right: 0 !important;
  bottom: 0 !important;
  left: 0 !important;
  z-index: 5000;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  padding: 14px 24px;
  border-radius: 0 !important;
  box-shadow: 0 -10px 28px rgba(15, 23, 42, 0.12);
  box-sizing: border-box;
}

.workflow-actions__hint {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #64748b;
  font-size: 13px;

  i { color: #2563eb; }

  &.is-reviewing {
    color: #1e40af;
    font-weight: 800;

    i { color: #2563eb; }
  }
}

.workflow-actions__buttons {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 10px;
}

@media screen and (max-width: 1024px) {
  .workflow-hero {
    grid-template-columns: 1fr;
  }

  .workflow-main-card__header,
  .workflow-actions {
    flex-direction: column;
    align-items: stretch;
  }
}
</style>
